import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { NewsEvent } from '../../types/game';

interface NewsBubbleTooltipProps {
  event: NewsEvent;
  cx: number;
  cy: number;
  /** 0 = first bubble (purple), 1 = second (pink), 2 = third (amber) */
  bubbleIndex: number;
  visible: boolean;
  /** Ref to the chart's SVG container so we can position the tooltip centered */
  chartContainerRef?: React.RefObject<HTMLDivElement | null>;
}

const BUBBLE_COLORS = ['#8b5cf6', '#ec4899', '#f59e0b'] as const; // purple, pink, amber

/** Generate a 5-point star path centered at (0,0) */
function starPath(outerR: number, innerR: number): string {
  const points: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    points.push(`${r * Math.cos(angle)},${r * Math.sin(angle)}`);
  }
  return `M${points.join('L')}Z`;
}

/**
 * A news event star marker rendered on the stock chart.
 * Shows a colored star that reveals a tooltip on hover (desktop) or tap (mobile).
 * The tooltip is portalled to document.body and centered horizontally on the chart.
 */
const NewsBubbleTooltip: React.FC<NewsBubbleTooltipProps> = ({
  event,
  cx,
  cy,
  bubbleIndex,
  visible,
  chartContainerRef,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasAppeared, setHasAppeared] = useState(false);
  const bubbleRef = useRef<SVGGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger stamp animation on mount
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => setHasAppeared(true), 50);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  // Close tooltip when clicking/tapping outside
  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        tooltipRef.current && !tooltipRef.current.contains(target) &&
        bubbleRef.current && !bubbleRef.current.contains(target)
      ) {
        setShowTooltip(false);
      }
    };

    // Use a slight delay so the same tap that opened it doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTooltip]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setShowTooltip(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setShowTooltip(false), 200);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setShowTooltip((prev) => !prev);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  if (!visible) return null;

  const color = BUBBLE_COLORS[bubbleIndex] ?? BUBBLE_COLORS[0];
  const sign = event.pctChange > 0 ? '+' : '';
  const pctLabel = `${sign}${event.pctChange}%`;
  const starOuter = 11;
  const starInner = 5;

  // Calculate tooltip position: centered on the chart, above the star
  const getTooltipPosition = (): React.CSSProperties => {
    const container = chartContainerRef?.current;
    if (!container) {
      // Fallback: position near the star using SVG coords
      const svg = bubbleRef.current?.closest('svg');
      if (!svg) return { display: 'none' };
      const svgRect = svg.getBoundingClientRect();
      return {
        position: 'fixed',
        left: svgRect.left + svgRect.width / 2,
        top: svgRect.top + svgRect.height * 0.15,
        transform: 'translateX(-50%)',
      };
    }
    const rect = container.getBoundingClientRect();
    return {
      position: 'fixed',
      left: rect.left + rect.width / 2,
      top: rect.top + rect.height * 0.08,
      transform: 'translateX(-50%)',
    };
  };

  return (
    <>
      <g ref={bubbleRef} transform={`translate(${cx},${cy})`}>
        {/* Shadow filter */}
        <defs>
          <filter id={`star-shadow-${bubbleIndex}`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="1" stdDeviation="2.5" floodColor={color} floodOpacity="0.5" />
          </filter>
        </defs>

        {/* Scale only — translate stays on the parent <g>'s SVG attribute. Putting translate in CSS too lets the CSS transform win, which resolves in a different frame than recharts' parent <g> and drifts the star off the line. */}
        <g
          style={{
            transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out',
            transform: hasAppeared ? 'scale(1)' : 'scale(0)',
            transformBox: 'fill-box',
            transformOrigin: 'center',
            opacity: hasAppeared ? 1 : 0,
          }}
        >
          {/* Pulsing glow behind star */}
          <circle
            r={16}
            fill={color}
            opacity={0.12}
            className="animate-pulse"
            style={{ pointerEvents: 'none' }}
          />

          {/* Star shape with shadow */}
          <path
            d={starPath(starOuter, starInner)}
            fill={color}
            stroke="white"
            strokeWidth={1.5}
            filter={`url(#star-shadow-${bubbleIndex})`}
            style={{ pointerEvents: 'none' }}
          />

          {/* Invisible hit area — captures all mouse/touch events with pointer cursor */}
          <circle
            r={16}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          />
        </g>
      </g>

      {/* Tooltip portalled to body — always above everything */}
      {showTooltip && createPortal(
        <div
          ref={tooltipRef}
          onMouseEnter={() => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
          }}
          onMouseLeave={() => {
            hoverTimeoutRef.current = setTimeout(() => setShowTooltip(false), 200);
          }}
          style={{
            ...getTooltipPosition(),
            zIndex: 99999,
            maxWidth: '300px',
            width: 'max-content',
            background: 'var(--clue-bg-card, #1a1a2e)',
            border: `1.5px solid ${color}`,
            borderRadius: '10px',
            padding: '10px 14px',
            boxShadow: `0 8px 24px rgba(0,0,0,0.4), 0 0 16px ${color}33`,
            pointerEvents: 'auto',
            userSelect: 'none',
            animation: 'news-tooltip-in 0.15s ease-out',
          }}
        >
          <style>{`
            @keyframes news-tooltip-in {
              from { opacity: 0; transform: translateX(-50%) translateY(4px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: 'var(--font-mono, monospace)',
                color: 'white',
                background: color,
              }}
            >
              {pctLabel}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--clue-text-secondary, #888)' }}>
              {event.date}
            </span>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '1.4',
              color: 'var(--clue-text-primary, #eee)',
            }}
          >
            {event.description}
          </p>
        </div>,
        document.body
      )}
    </>
  );
};

export default React.memo(NewsBubbleTooltip);
